import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, Loader2, Lock,
} from "lucide-react";

// Ledger Master — Sprint 1 (Trial Balance Audit & Enhancement).
//
// Promoted from "JV Ledger Master" to the system-wide chart of accounts.
// Every ledger is strictly defined: unique Code + Name, Schedule III
// Group, Nature, Schedule III mapping, and a System-vs-User flag.
// System ledgers back the GL posting engine — their structure is locked.

const _TYPES = ["Asset", "Liability", "Expense", "Income"];
const _NATURES = ["Debit", "Credit"];

const _typeBadge = (t) => ({
  Asset: "bg-sky-100 text-sky-800",
  Liability: "bg-amber-100 text-amber-800",
  Expense: "bg-rose-100 text-rose-800",
  Income: "bg-emerald-100 text-emerald-800",
}[t] || "bg-slate-100 text-slate-700");

const _emptyLedger = () => ({
  code: "",
  name: "",
  type: "Expense",
  group: "",
  schedule_iii_mapping: "",
  default_nature: "Debit",
  active: true,
  branch_applicability: [],
  description: "",
});

export default function JVLedgersPage() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [branches, setBranches] = useState([]);
  const [groupsMeta, setGroupsMeta] = useState({ canonical: {}, in_use: [] });
  const [filters, setFilters] = useState({
    type: "", active: "true", search: "",
  });

  const buildQuery = () => {
    const p = new URLSearchParams();
    p.set("page", String(page));
    p.set("page_size", String(pageSize));
    if (filters.type) p.set("type", filters.type);
    if (filters.active !== "") p.set("active", filters.active);
    if (filters.search) p.set("search", filters.search);
    return p.toString();
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/jv-ledgers?${buildQuery()}`);
      setRows(r.data?.items || []);
      setTotal(r.data?.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load ledgers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/branches").then((r) => setBranches(r.data || [])).catch(() => {});
    api.get("/jv-ledger-groups").then((r) => setGroupsMeta(r.data || { canonical: {}, in_use: [] })).catch(() => {});
  }, []);

  useEffect(() => {
    load();
  }, [page, pageSize, filters]);

  const onNew = () => { setEditing(_emptyLedger()); setShowForm(true); };
  const onEdit = (row) => { setEditing({ ...row }); setShowForm(true); };
  const onDelete = async (row) => {
    if (!window.confirm(
      `Delete ledger "${row.code} — ${row.name}"?\n\n` +
      `If any Journal Voucher references it, the delete will be blocked and you'll be asked to mark it Inactive instead.`,
    )) return;
    try {
      await api.delete(`/jv-ledgers/${row.id}`);
      toast.success("Ledger deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };
  const toggleActive = async (row) => {
    if (row.is_system && row.active) {
      toast.error("System ledgers cannot be deactivated — required by the GL posting engine");
      return;
    }
    try {
      await api.put(`/jv-ledgers/${row.id}`, { active: !row.active });
      toast.success(row.active ? "Ledger deactivated" : "Ledger activated");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    }
  };

  const totalsByType = useMemo(() => {
    const acc = { Asset: 0, Liability: 0, Expense: 0, Income: 0 };
    rows.forEach((r) => { if (acc[r.type] !== undefined) acc[r.type]++; });
    return acc;
  }, [rows]);

  return (
    <div className="p-6 space-y-4" data-testid="jv-ledgers-page">
      <div className="flex items-start justify-between gap-6">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold text-slate-900">Ledger Master</h1>
          <p className="text-sm text-slate-500">
            Single source of truth for the chart of accounts. Every ledger is
            strictly defined — unique Code &amp; Name, Schedule III Group,
            Nature and mapping. System ledgers (locked) back the GL posting
            engine; user ledgers serve Journal Vouchers.
          </p>
        </div>
        <div
          className="hidden md:flex flex-col text-xs bg-slate-50 border border-slate-200 rounded-lg px-4 py-2 min-w-[280px]"
          data-testid="jv-ledgers-summary"
        >
          <div className="flex justify-between py-0.5">
            <span className="text-slate-500">Total shown</span>
            <span className="font-semibold text-slate-800" data-testid="jv-ledgers-total">{total}</span>
          </div>
          {_TYPES.map((t) => (
            <div key={t} className="flex justify-between py-0.5">
              <span className="text-slate-500">{t}</span>
              <span className="font-semibold text-slate-700">{totalsByType[t]}</span>
            </div>
          ))}
        </div>
        <Button
          onClick={onNew}
          className="bg-orange-600 hover:bg-orange-700 text-white"
          data-testid="jv-ledgers-new-btn"
        >
          <Plus className="w-4 h-4 mr-1" /> New Ledger
        </Button>
      </div>

      {/* Filter bar */}
      <div
        className="bg-white border border-slate-200 rounded-lg p-3 grid grid-cols-1 md:grid-cols-4 gap-2 text-xs"
        data-testid="jv-ledgers-filter-bar"
      >
        <div>
          <label className="text-slate-500 font-medium">Type</label>
          <select
            className="w-full border rounded px-2 py-1"
            value={filters.type}
            onChange={(e) => { setFilters({ ...filters, type: e.target.value }); setPage(1); }}
            data-testid="jv-ledgers-filter-type"
          >
            <option value="">All</option>
            {_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className="text-slate-500 font-medium">Active</label>
          <select
            className="w-full border rounded px-2 py-1"
            value={filters.active}
            onChange={(e) => { setFilters({ ...filters, active: e.target.value }); setPage(1); }}
            data-testid="jv-ledgers-filter-active"
          >
            <option value="">All</option>
            <option value="true">Active only</option>
            <option value="false">Inactive only</option>
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="text-slate-500 font-medium">Search (Code / Name)</label>
          <Input
            className="text-xs py-1 h-8"
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            onKeyDown={(e) => e.key === "Enter" && setPage(1)}
            placeholder="type & press Enter…"
            data-testid="jv-ledgers-filter-search"
          />
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-700">
            <tr>
              <th className="px-3 py-2 text-left">Code</th>
              <th className="px-3 py-2 text-left">Name</th>
              <th className="px-3 py-2 text-left">Type</th>
              <th className="px-3 py-2 text-left">Group</th>
              <th className="px-3 py-2 text-left">Schedule III</th>
              <th className="px-3 py-2 text-left">Default</th>
              <th className="px-3 py-2 text-left">Branches</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan="9" className="p-6 text-center text-slate-500">
                <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…
              </td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan="9" className="p-6 text-center text-slate-500" data-testid="jv-ledgers-empty-state">
                  No ledgers match the current filters. Try clearing filters or add a new one.
                </td>
              </tr>
            )}
            {!loading && rows.map((r) => (
              <tr key={r.id} className="border-t hover:bg-slate-50" data-testid={`jv-ledger-row-${r.id}`}>
                <td className="px-3 py-2 font-mono text-xs">
                  <div className="flex items-center gap-1.5">
                    {r.code}
                    {r.is_system && (
                      <span
                        className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 border border-violet-200 inline-flex items-center gap-0.5"
                        data-testid={`jv-ledger-system-badge-${r.id}`}
                        title="System ledger — required by the GL posting engine. Structure locked."
                      ><Lock className="w-2.5 h-2.5" /> SYSTEM</span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2 font-medium">{r.name}</td>
                <td className="px-3 py-2">
                  <Badge className={_typeBadge(r.type)}>{r.type}</Badge>
                </td>
                <td className="px-3 py-2 text-xs text-slate-600">{r.group || "—"}</td>
                <td className="px-3 py-2 text-xs text-slate-600 max-w-[240px] truncate" title={r.schedule_iii_mapping}>
                  {r.schedule_iii_mapping || "—"}
                </td>
                <td className="px-3 py-2 text-xs">{r.default_nature}</td>
                <td className="px-3 py-2 text-xs text-slate-500">
                  {(r.branch_applicability || []).length === 0
                    ? "All"
                    : `${r.branch_applicability.length} branch(es)`}
                </td>
                <td className="px-3 py-2">
                  <button
                    onClick={() => toggleActive(r)}
                    data-testid={`jv-ledger-toggle-${r.id}`}
                    className={`text-xs px-2 py-0.5 rounded ${
                      r.active
                        ? "bg-emerald-100 text-emerald-800"
                        : "bg-slate-200 text-slate-500"
                    } ${r.is_system && r.active ? "cursor-not-allowed opacity-70" : ""}`}
                  >{r.active ? "Active" : "Inactive"}</button>
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="flex justify-end gap-1">
                    <button
                      className="p-1 text-blue-600 hover:text-blue-800"
                      onClick={() => onEdit(r)}
                      title="Edit"
                      data-testid={`jv-ledger-edit-${r.id}`}
                    ><Pencil className="w-4 h-4" /></button>
                    {!r.is_system && (
                      <button
                        className="p-1 text-rose-500 hover:text-rose-700"
                        onClick={() => onDelete(r)}
                        title="Delete (blocked if in use)"
                        data-testid={`jv-ledger-delete-${r.id}`}
                      ><Trash2 className="w-4 h-4" /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-xs text-slate-600 pt-1">
        <div>
          Showing <span className="font-semibold">{rows.length}</span> of{" "}
          <span className="font-semibold">{total}</span> ledgers
          {rows.length ? ` · page ${page} / ${Math.max(1, Math.ceil(total / pageSize))}` : ""}
        </div>
        <div className="flex items-center gap-2">
          <button
            className="p-1 border rounded disabled:opacity-40"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            data-testid="jv-ledgers-pagination-prev"
          >‹ Prev</button>
          <button
            className="p-1 border rounded disabled:opacity-40"
            disabled={page * pageSize >= total}
            onClick={() => setPage((p) => p + 1)}
            data-testid="jv-ledgers-pagination-next"
          >Next ›</button>
        </div>
      </div>

      {showForm && editing && (
        <LedgerForm
          initial={editing}
          branches={branches}
          groupsMeta={groupsMeta}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={async () => { setShowForm(false); setEditing(null); await load(); }}
        />
      )}
    </div>
  );
}


function LedgerForm({ initial, branches, groupsMeta, onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const isEdit = !!initial.id;
  const isSystem = !!initial.is_system;

  const groupOptions = useMemo(() => {
    const canonical = (groupsMeta?.canonical || {})[form.type] || [];
    const inUse = groupsMeta?.in_use || [];
    return [...new Set([...canonical, ...inUse])];
  }, [groupsMeta, form.type]);

  const submit = async () => {
    if (!form.code.trim()) return toast.error("Code is required");
    if (!form.name.trim()) return toast.error("Name is required");
    if (!(form.group || "").trim()) return toast.error("Group is required (Schedule III grouping)");
    if (!(form.schedule_iii_mapping || "").trim()) return toast.error("Schedule III mapping is required");
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/jv-ledgers/${form.id}`, form);
        toast.success("Ledger updated");
      } else {
        await api.post("/jv-ledgers", form);
        toast.success("Ledger created");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleBranch = (bid) => {
    setForm((f) => {
      const arr = new Set(f.branch_applicability || []);
      if (arr.has(bid)) arr.delete(bid); else arr.add(bid);
      return { ...f, branch_applicability: [...arr] };
    });
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="jv-ledger-form-modal">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit" : "New"} Ledger</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Ledgers are the single source of truth for account rollups.
            Code (uppercased) and Name are unique. Group, Nature and
            Schedule III mapping are mandatory.
          </DialogDescription>
        </DialogHeader>

        {isSystem && (
          <div
            className="text-xs bg-violet-50 border border-violet-200 text-violet-800 rounded px-3 py-2 flex items-center gap-2"
            data-testid="jv-ledger-system-notice"
          >
            <Lock className="w-3.5 h-3.5 shrink-0" />
            System ledger — Code, Type and Nature are locked; it cannot be
            deactivated or deleted. Name, Group, Schedule III and Description
            remain editable.
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-slate-500 font-medium">Code *</label>
            <Input
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
              placeholder="e.g. EB_BONUS_EXP"
              className="font-mono"
              disabled={isSystem}
              data-testid="jv-ledger-code"
            />
          </div>
          <div>
            <label className="text-xs text-slate-500 font-medium">Name *</label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Bonus Expense"
              data-testid="jv-ledger-name"
            />
          </div>
          <div>
            <label className="text-xs text-slate-500 font-medium">Type *</label>
            <select
              className="w-full border rounded px-2 py-1.5 text-sm disabled:bg-slate-100 disabled:text-slate-500"
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
              disabled={isSystem}
              data-testid="jv-ledger-type"
            >
              {_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500 font-medium">Default Nature *</label>
            <select
              className="w-full border rounded px-2 py-1.5 text-sm disabled:bg-slate-100 disabled:text-slate-500"
              value={form.default_nature}
              onChange={(e) => setForm({ ...form, default_nature: e.target.value })}
              disabled={isSystem}
              data-testid="jv-ledger-default-nature"
            >
              {_NATURES.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div className="col-span-2">
            <label className="text-xs text-slate-500 font-medium">Group * <span className="text-slate-400 font-normal">(Schedule III grouping)</span></label>
            <Input
              value={form.group || ""}
              onChange={(e) => setForm({ ...form, group: e.target.value })}
              placeholder="e.g. Other Expenses"
              list="ledger-group-options"
              data-testid="jv-ledger-group"
            />
            <datalist id="ledger-group-options">
              {groupOptions.map((g) => <option key={g} value={g} />)}
            </datalist>
          </div>
          <div className="col-span-2">
            <label className="text-xs text-slate-500 font-medium">Schedule III Mapping *</label>
            <Input
              value={form.schedule_iii_mapping}
              onChange={(e) => setForm({ ...form, schedule_iii_mapping: e.target.value })}
              placeholder="e.g. Other expenses — Payments to auditors — Audit fees"
              data-testid="jv-ledger-schedule-iii"
            />
          </div>
          <div className="col-span-2">
            <label className="text-xs text-slate-500 font-medium">Description</label>
            <Textarea
              rows={2}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Optional. When would Finance pick this ledger?"
              data-testid="jv-ledger-description"
            />
          </div>
          <div className="col-span-2">
            <label className="text-xs text-slate-500 font-medium">
              Branch Applicability{" "}
              <span className="text-slate-400 font-normal">
                (leave empty to make it available to ALL branches)
              </span>
            </label>
            <div className="flex flex-wrap gap-2 mt-1">
              {branches.map((b) => {
                const selected = (form.branch_applicability || []).includes(b.id);
                return (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => toggleBranch(b.id)}
                    data-testid={`jv-ledger-branch-${b.id}`}
                    className={`text-xs px-3 py-1 rounded-full border ${
                      selected
                        ? "bg-orange-600 text-white border-orange-600"
                        : "bg-white border-slate-300 text-slate-600"
                    }`}
                  >{b.branch_name}</button>
                );
              })}
            </div>
          </div>
          <div className="col-span-2">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={!!form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
                disabled={isSystem}
                data-testid="jv-ledger-active"
              />
              <span>Active (available for selection in new JVs)</span>
            </label>
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={saving}
            className="bg-orange-600 hover:bg-orange-700 text-white"
            data-testid="jv-ledger-save-btn"
          >
            {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            {isEdit ? "Update Ledger" : "Create Ledger"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
